/**
 * /declaraties — bonnen die medewerkers privé hebben voorgeschoten (team-app).
 * Kantoor keurt goed of wijst af, maakt over per bank en vinkt 'uitbetaald'.
 * Bij goedkeuring gaat de bon automatisch naar de Yuki-inbox; mislukt dat,
 * dan staat het hier met een knop 'Opnieuw naar Yuki'. Leest /api/workforce/declaraties.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../components/ui/Card';
import { supabase } from '../lib/supabase';

type Status = 'ingediend' | 'goedgekeurd' | 'afgewezen' | 'uitbetaald' | 'ingetrokken';

interface Declaratie {
  id: string;
  nummer: string;
  staff_id: string;
  datum: string;
  categorie: string;
  bedrag: number;
  omschrijving: string | null;
  bon_mime: string;
  bon_url: string | null;
  status: Status;
  beoordeeld_door: string | null;
  beoordeeld_op: string | null;
  afwijsreden: string | null;
  uitbetaald_door: string | null;
  uitbetaald_op: string | null;
  uitbetaald_via: 'bank' | 'kas' | null;
  yuki_verzonden_op: string | null;
  yuki_fout: string | null;
  created_at: string;
  staff: { name: string | null; team: string | null; email: string | null; bankrekening: string | null } | null;
}

async function officeFetch(path: string, method = 'GET', body?: unknown): Promise<unknown> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `API error: ${response.status}`);
  }
  return response.json();
}

const euro = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);
const datumNl = (d: string) => new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

const CATEGORIE_LABEL: Record<string, string> = {
  eten: 'Eten & drinken',
  brandstof: 'Brandstof',
  tol: 'Tol',
  parkeren: 'Parkeren',
  overig: 'Overig',
};

const STATUS: Record<Status, { label: string; cls: string }> = {
  ingediend: { label: 'Te beoordelen', cls: 'bg-amber-100 text-amber-800' },
  goedgekeurd: { label: 'Overmaken', cls: 'bg-sky-100 text-sky-800' },
  uitbetaald: { label: 'Uitbetaald', cls: 'bg-emerald-100 text-emerald-800' },
  afgewezen: { label: 'Afgewezen', cls: 'bg-red-100 text-red-800' },
  ingetrokken: { label: 'Ingetrokken', cls: 'bg-slate-100 text-slate-600' },
};

type Filter = 'open' | 'uitbetaald' | 'afgewezen' | 'alles';

export function DeclaratiesPage() {
  const [filter, setFilter] = useState<Filter>('open');
  const [rows, setRows] = useState<Declaratie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bezig, setBezig] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReden, setRejectReden] = useState('');
  const [melding, setMelding] = useState<string | null>(null);

  const laad = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await officeFetch(`/api/workforce/declaraties?status=${filter}`)) as { declaraties: Declaratie[] };
      setRows(data.declaraties || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    laad();
  }, [laad]);

  const actie = async (d: Declaratie, body: Record<string, unknown>, succes: string) => {
    setBezig(d.id);
    setMelding(null);
    try {
      const res = (await officeFetch(`/api/workforce/declaraties?id=${d.id}`, 'PATCH', body)) as { yuki?: { verzonden: boolean; reden?: string } };
      let tekst = `${d.nummer}: ${succes}`;
      if (res.yuki && !res.yuki.verzonden) tekst += `. Let op: bon NIET bij Yuki aangekomen (${res.yuki.reden || 'onbekend'}), gebruik 'Opnieuw naar Yuki'`;
      setMelding(tekst);
      setRejectId(null);
      setRejectReden('');
      await laad();
    } catch (e) {
      setMelding(`${d.nummer}: ${e instanceof Error ? e.message : 'mislukt'}`);
    } finally {
      setBezig(null);
    }
  };

  const teBeoordelen = useMemo(() => rows.filter(r => r.status === 'ingediend'), [rows]);
  const overmaken = useMemo(() => rows.filter(r => r.status === 'goedgekeurd'), [rows]);
  const overig = useMemo(() => rows.filter(r => r.status !== 'ingediend' && r.status !== 'goedgekeurd'), [rows]);
  const totaalOvermaken = overmaken.reduce((s, r) => s + Number(r.bedrag), 0);

  const Rij = ({ d }: { d: Declaratie }) => (
    <div className="flex flex-col md:flex-row md:items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-900">{d.staff?.name || 'Onbekend'}</span>
          <span className="text-slate-400 text-xs">{d.staff?.team}</span>
          <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', STATUS[d.status].cls)}>{STATUS[d.status].label}</span>
        </div>
        <div className="text-sm text-slate-700 mt-0.5">
          <span className="font-semibold">{euro(Number(d.bedrag))}</span> · {CATEGORIE_LABEL[d.categorie] || d.categorie} · {datumNl(d.datum)} · <span className="font-mono text-xs">{d.nummer}</span>
        </div>
        {d.omschrijving && <div className="text-sm text-slate-600 mt-0.5">{d.omschrijving}</div>}
        <div className="text-xs text-slate-400 mt-1 space-x-2">
          <span>Ingediend {datumNl(d.created_at)}</span>
          {d.beoordeeld_op && <span>· {d.status === 'afgewezen' ? 'Afgewezen' : 'Goedgekeurd'} {datumNl(d.beoordeeld_op)} door {d.beoordeeld_door}</span>}
          {d.uitbetaald_op && <span>· Uitbetaald {datumNl(d.uitbetaald_op)} ({d.uitbetaald_via}) door {d.uitbetaald_door}</span>}
          {d.afwijsreden && <span>· Reden: {d.afwijsreden}</span>}
        </div>
        {(d.status === 'goedgekeurd' || d.status === 'uitbetaald') && (
          <div className={clsx('text-xs mt-1', d.yuki_verzonden_op ? 'text-emerald-700' : 'text-red-700')}>
            {d.yuki_verzonden_op ? `Bon bij Yuki (${datumNl(d.yuki_verzonden_op)})` : `Bon niet bij Yuki${d.yuki_fout ? `: ${d.yuki_fout}` : ''}`}
          </div>
        )}
        {d.status === 'goedgekeurd' && (
          <div className="text-xs text-slate-600 mt-1">
            Overmaken naar <span className="font-mono">{d.staff?.bankrekening || 'IBAN onbekend, zie personeelskaart'}</span> met kenmerk <span className="font-mono">{d.nummer}</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {d.bon_url && (
          <a href={d.bon_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
            Bon
          </a>
        )}
        {d.status === 'ingediend' && rejectId !== d.id && (
          <>
            <button
              disabled={bezig === d.id}
              onClick={() => actie(d, { action: 'approve' }, 'goedgekeurd, bon naar Yuki')}
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Goedkeuren
            </button>
            <button
              disabled={bezig === d.id}
              onClick={() => { setRejectId(d.id); setRejectReden(''); }}
              className="px-3 py-1.5 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Afwijzen
            </button>
          </>
        )}
        {d.status === 'ingediend' && rejectId === d.id && (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={rejectReden}
              onChange={e => setRejectReden(e.target.value)}
              placeholder="Reden (ziet de medewerker)"
              className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg w-56"
            />
            <button
              disabled={!rejectReden.trim() || bezig === d.id}
              onClick={() => actie(d, { action: 'reject', reden: rejectReden.trim() }, 'afgewezen')}
              className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              Bevestig
            </button>
            <button onClick={() => setRejectId(null)} className="text-sm text-slate-500 hover:text-slate-800">Annuleer</button>
          </div>
        )}
        {d.status === 'goedgekeurd' && (
          <>
            <button
              disabled={bezig === d.id}
              onClick={() => actie(d, { action: 'uitbetaald', via: 'bank' }, 'gemarkeerd als overgemaakt')}
              className="px-3 py-1.5 text-sm rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
            >
              Overgemaakt
            </button>
            <button
              disabled={bezig === d.id}
              onClick={() => actie(d, { action: 'uitbetaald', via: 'kas' }, 'contant uitbetaald')}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              title="Contant meegegeven uit de kas"
            >
              Contant
            </button>
          </>
        )}
        {(d.status === 'goedgekeurd' || d.status === 'uitbetaald') && !d.yuki_verzonden_op && (
          <button
            disabled={bezig === d.id}
            onClick={() => actie(d, { action: 'yuki' }, 'bon naar Yuki verstuurd')}
            className="px-3 py-1.5 text-sm rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
          >
            Opnieuw naar Yuki
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Declaraties</h1>
          <p className="text-sm text-slate-500">Privé voorgeschoten bonnen uit de team-app. Goedkeuren stuurt de bon naar Yuki; daarna overmaken en afvinken.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {(['open', 'uitbetaald', 'afgewezen', 'alles'] as Filter[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={clsx('px-3 py-1 text-sm rounded-md capitalize', filter === f ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900')}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {melding && <div className="text-sm px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700">{melding}</div>}
      {error && <div className="text-sm px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">{error}</div>}

      {loading ? (
        <div className="text-sm text-slate-500">Laden…</div>
      ) : filter === 'open' ? (
        <>
          <Card>
            <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Te beoordelen ({teBeoordelen.length})</div>
            <div className="px-4">
              {teBeoordelen.length === 0 ? <p className="text-sm text-slate-500 py-3">Niets te beoordelen.</p> : teBeoordelen.map(d => <Rij key={d.id} d={d} />)}
            </div>
          </Card>
          <Card>
            <div className="px-4 pt-3 pb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overmaken ({overmaken.length})</span>
              {overmaken.length > 0 && <span className="text-xs text-slate-500">Totaal {euro(totaalOvermaken)}</span>}
            </div>
            <div className="px-4">
              {overmaken.length === 0 ? <p className="text-sm text-slate-500 py-3">Alles is overgemaakt.</p> : overmaken.map(d => <Rij key={d.id} d={d} />)}
            </div>
          </Card>
        </>
      ) : (
        <Card>
          <div className="px-4">
            {rows.length === 0 ? <p className="text-sm text-slate-500 py-3">Geen declaraties.</p> : [...teBeoordelen, ...overmaken, ...overig].map(d => <Rij key={d.id} d={d} />)}
          </div>
        </Card>
      )}
    </div>
  );
}
